import { useUpdateAttachmentComment } from 'src/api/queries/workflowModules/useAttachmentMutations';

export interface IWorkflowAttachmentLike {
  id: string;
  comment?: string | undefined;
}

export interface IWorkflowAttachmentValues<
  TFile extends IWorkflowAttachmentLike,
> {
  attachments?: TFile[] | undefined;
  filesToAdd: TFile[];
}

/**
 * @registry Attachment editors shared by the workflow forms: persists an inline
 * FileTable description through the attachment comment mutation, and folds a
 * document picked from the job's documents into the form's attachments plus its
 * pending filesToAdd. Pass formik's setValues.
 */
export function useWorkflowAttachmentEdits<
  TFile extends IWorkflowAttachmentLike,
  TValues extends IWorkflowAttachmentValues<TFile>,
>(setValues: (updater: (values: TValues) => TValues) => void, jobId: number) {
  const updateComment = useUpdateAttachmentComment();

  const saveComment = async (file: { id: string }, comment: string) => {
    const updated = await updateComment
      .mutateAsync({ jobId, attachmentId: file.id, comment })
      .catch(() => null);
    if (!updated) return;
    setValues((v) => ({
      ...v,
      attachments: (v.attachments ?? []).map((a) =>
        a.id === file.id ? { ...a, comment: updated.comment ?? comment } : a,
      ),
    }));
  };

  const addDocument = (attachment: TFile) => {
    setValues((v) => ({
      ...v,
      attachments: [...(v.attachments ?? []), attachment],
      filesToAdd: [...v.filesToAdd, attachment],
    }));
  };

  return { saveComment, addDocument };
}
